import React, {useEffect, useState} from 'react';
import {PlayerProps} from './Player.utils';
import {UserComment} from 'records/UserComment';
import {SetState} from '@store/@types/util/React';
import {registerEvents} from 'utils/registerEvents';
import {UnsavedCallComment} from '@components/call-card/UnsavedCallComment';
import './NewUserComment.sass';
import AuthStore from 'stores/AuthStore';

interface NewUserCommentProps extends PlayerProps {
  setNewComment?: SetState<UserComment | undefined>;
  track: HTMLDivElement | null;
}

export const NewUserComment: React.FC<NewUserCommentProps> = ({duration, setNewComment, track}) => {
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [comment, setComment] = useState<UserComment>();

  useEffect(() => {
    if (dragStart === null) return;
    const moveFn = (e: MouseEvent) => {
      if (dragStart === null) return;
      if (!track) return;

      const {x, width} = track.getBoundingClientRect();
      const newPct = (e.clientX - x) / width;
      const dragStartPct = (dragStart - x) / width;

      const newTime = newPct * duration;
      const dragStartTime = dragStartPct * duration;

      setComment(c => {
        if (!c) return c;

        if (e.clientX < dragStart) {
          // user drags left of start. use dragStart as comment.end
          return c.set('startTime', newTime).set('endTime', dragStartTime);
        } else if (e.clientX > dragStart) {
          // user drags right of start. use dragStart as comment.start
          return c.set('startTime', dragStartTime).set('endTime', newTime);
        } else {
          return c.set('startTime', dragStartTime).set('endTime', dragStartTime);
        }
      });
    };
    const mouseUp = () => {
      if (dragStart === null) return;
      setDragStart(null);
      setNewComment?.(comment);
      // Timeout here so the comment doesn't disappear before the modal appears
      setTimeout(() => {
        setComment(undefined);
      }, 500);
    };

    document.addEventListener('mousemove', moveFn);
    document.addEventListener('mouseup', mouseUp);

    return () => {
      document.removeEventListener('mousemove', moveFn);
      document.removeEventListener('mouseup', mouseUp);
    };
  }, [dragStart, setDragStart, setComment, duration, comment, setNewComment, track]);

  // Listen for Mouse Down to begin comment creation
  useEffect(() => {
    if (!track) return;

    const createCommentHandler = e => {
      if (!track) return;
      if (e.target.classList.contains('anchor')) {
        /**
         * This is an event from a highlight resize. Ignore it.
         * @link CallRegion
         */
        return;
      }
      if (e.target.classList.contains('comment')) {
        /**
         * This is an event from clicking a comment or highlight. Ignore it.
         * @link CallComment
         */
        return;
      }

      const {x, width} = track.getBoundingClientRect();
      const startPct = (e.clientX - x) / width;
      const startTime = startPct * duration;
      setDragStart(e.clientX);
      setComment(
        new UserComment({
          message: '',
          userId: AuthStore.getState().user.id,
          startTime: startTime,
          endTime: startTime,
        })
      );
    };

    return registerEvents(track, [['mousedown', createCommentHandler]]);
  }, [duration, track]);

  return (
    <>
      <div className="new-user-comment-wrapper">
        <div className="click-gutter"></div>
      </div>
      {comment && <UnsavedCallComment comment={comment} duration={duration} />}
    </>
  );
};
